/**
 * GameStartCountdown Component
 *
 * Animated countdown displayed when both players are ready and the game is starting.
 *
 * Features:
 * - Shows "3... 2... 1... Go!" countdown
 * - Smooth animations
 * - Automatically dismisses after countdown
 * - Full-screen overlay for dramatic effect
 *
 * Phase 6.2 Implementation
 */

import { useState, useEffect } from 'react';
import { COPY } from '../copy';

export default function GameStartCountdown({ isActive, onComplete }) {
  const [count, setCount] = useState(3);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setIsVisible(false);
      setCount(3);
      return;
    }

    setIsVisible(true);

    // Countdown sequence: 3, 2, 1, "Go!"
    const countdownTimer = setInterval(() => {
      setCount((prev) => {
        if (prev > 1) {
          return prev - 1;
        }
        return 0; // "Go!" state
      });
    }, 1000);

    // Complete after 4 seconds (3, 2, 1, Go!)
    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) {
        onComplete();
      }
    }, 4000);

    return () => {
      clearInterval(countdownTimer);
      clearTimeout(completeTimer);
    };
  }, [isActive, onComplete]);

  if (!isVisible) {
    return null;
  }

  const displayText = count > 0 ? count : COPY.modal.countdown.numbers[3];
  const isGo = count === 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 transition-opacity duration-500"
      data-testid="game-start-countdown"
    >
      <div
        className={`text-center transition-all duration-500 transform ${isGo ? 'scale-150' : 'scale-100'}`}
      >
        <div
          className={`text-9xl font-bold ${isGo ? 'text-green-400' : 'text-white'} animate-bounce`}
          data-testid="countdown-number"
        >
          {displayText}
        </div>
        <p className="text-white text-2xl mt-4 animate-pulse">
          {isGo ? COPY.modal.countdown.gameStarting : COPY.modal.countdown.getReady}
        </p>
      </div>
    </div>
  );
}
